import { registerBlock } from "../../registry";
import { makeId } from "../../helpers";
import type { ContentBlock } from "../../types";

const comprehensiveGenomic: ContentBlock<"module"> = {
  id: makeId("module-comprehensive-genomic"),
  kind: "module",
  tags: [
    "both-partners",
    "testing",
    "genetic-screening",
    "genomic",
    "pharmacogenomics",
  ],
  title: "Genomic Profiling",
  subtitle:
    "Whole exome or whole genome sequencing with pharmacogenomics — far beyond carrier screening",
  icon: "🧬",
  body: {
    duration: "6–12 weeks",
    category: "testing",
    forWhom: "both",
    includes: [
      "Whole exome sequencing (WES) or whole genome sequencing (WGS) for each partner",
      "Pharmacogenomic panel — how you metabolize fertility medications (letrozole, gonadotropins, progesterone)",
      "Polygenic risk scores for fertility-relevant conditions (PMOS (ex PCOS), endometriosis, premature ovarian insufficiency, recurrent loss)",
      "Monogenic variant screening beyond standard carrier panels (rare variants, VUS review)",
      "Genetic counselor deep-dive session to interpret complex findings",
      "Actionable medication and treatment-response insights based on your genome",
    ],
    trackableMarkers: [
      {
        name: "Pharmacogenomic profile",
        expectedToChange: false,
        expectation:
          "Your genetic medication response profile is fixed — but it changes how your doctor doses letrozole, gonadotropins, and other fertility drugs. Identified once, used for every future cycle.",
      },
      {
        name: "Polygenic risk scores",
        expectedToChange: false,
        expectation:
          "Risk scores for PMOS (ex PCOS), endometriosis, POI, and recurrent miscarriage. These don't change, but they inform proactive screening and treatment planning.",
      },
      {
        name: "Monogenic variants",
        expectedToChange: false,
        expectation:
          "Rare variants that may affect fertility, implantation, or pregnancy outcomes. Goes beyond standard carrier screening to catch variants a panel wouldn't find.",
      },
    ],
    priceRange: { low: 1500, high: 5000 },
    currency: "USD",
    status: "coming-soon",
    steps: [
      {
        title: "Sample collection",
        description:
          "Blood draw or saliva kit for each partner. Can be done at a lab or at home with a mail-in kit.",
      },
      {
        title: "Sequencing",
        description:
          "Whole exome or whole genome sequencing. Turnaround is 4–8 weeks depending on the lab and depth of sequencing.",
      },
      {
        title: "Bioinformatics analysis",
        description:
          "Pharmacogenomic profiling, polygenic risk scoring, and variant interpretation by a clinical genetics team.",
      },
      {
        title: "Genetic counseling",
        description:
          "60-minute session with a genetic counselor to walk through all findings — medication response, risk scores, and any actionable variants.",
      },
    ],
  },
  sources: [
    {
      title:
        "Clinical utility of pharmacogenomic testing in reproductive medicine",
      journal: "Fertility and Sterility",
      year: 2023,
      evidenceLevel: "observational",
    },
    {
      title:
        "Whole exome sequencing in reproductive genetics: current applications and future directions",
      journal: "Human Reproduction Update",
      year: 2022,
      evidenceLevel: "review",
    },
  ],
};

registerBlock(comprehensiveGenomic);
